import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const DISMISS_KEY = "mfaGraceNoticeDismissed";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch (e) {
    return false;
  }
};

/**
 * Reminder for users whose organization requires two-step verification and who are still inside the
 * grace period. It asks the server (GET /mfa/status), so it disappears by itself once they set it up,
 * and it never appears for users it does not concern. It floats in the corner, so no page layout changes.
 */
const MfaGraceNotice = () => {
  const { pathname } = useLocation();
  const layoutMode = useSelector((state) => (state.Layout ? state.Layout.layoutMode : "light"));
  const isDark = layoutMode === "dark";
  const [daysLeft, setDaysLeft] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed());

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || dismissed) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_DEFAULTPATH}mfa/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        const show = data && data.featureEnabled && data.required && !data.enabled && data.graceDaysLeft;
        setDaysLeft(show ? data.graceDaysLeft : null);
      } catch (e) {
        if (alive) setDaysLeft(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [pathname, dismissed]);

  if (!daysLeft || dismissed || pathname === "/myprofile") return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      // no storage: it only hides until the next page load
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 1050,
        width: 330,
        maxWidth: "calc(100vw - 40px)",
        padding: "14px 16px",
        borderRadius: 10,
        background: isDark ? "#1a2234" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
        border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
        borderLeft: "4px solid #f59e0b",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Set up two-step verification</div>
      <div style={{ marginBottom: 10 }}>
        Your organization requires it. You have <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong> left; after that you will be asked to set it up before you can sign in.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Link to="/myprofile" className="btn btn-primary btn-sm">Set up now</Link>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={dismiss}>Later</button>
      </div>
    </div>
  );
};

export default MfaGraceNotice;
